/**
 * Unified notification surfaces: `useNotificationsFeed` is the single React Query source for
 * announcements plus support unread counts (nav badges and the bell share this). `useNotifications`
 * layers dashboard strip alerts on top for severity grouping and dismiss state.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo, useSyncExternalStore } from 'react'
import {
  getDashboardDismissedSnapshot,
  parseDashboardDismissedSnapshot,
  subscribeDashboardDismissed,
} from '../constants/dashboardAlertsDismiss'
import { useAuth } from '../contexts/AuthContext'
import type { DashboardAlert } from '../pages/Dashboard/useDashboardAlerts'
import { useDashboardAlerts } from '../pages/Dashboard/useDashboardAlerts'
import {
  normalizeDashboardStats,
  parseDashboardStatsPayload,
} from '../pages/Dashboard/dashboardTypes'
import { api } from '../utils/api'
import { announcementsService, type Announcement } from '../services/announcements.service'
import { supportService } from '../services/support.service'
import { reportError } from '../utils/reportError'
import { trackObservability } from '../utils/telemetry'

/** Re-export for callers that imported the key from this module. */
export { DASHBOARD_ALERTS_DISMISS_KEY } from '../constants/dashboardAlertsDismiss'

export type NotificationSurfaceItem = {
  id: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  body: string
  href?: string
  source: 'announcement' | 'support_unread' | 'dashboard'
}

export type NotificationsFeed = {
  announcements: Announcement[]
  unreadTicketCount: number
  loading: boolean
  announcementsFailed: boolean
  ticketCountFailed: boolean
  refetch: () => Promise<unknown>
}

export function useNotificationsFeed(): NotificationsFeed {
  const { user, isLoading: authLoading } = useAuth()

  const query = useQuery({
    queryKey: ['notifications-feed', user?.id, user?.role],
    enabled: !authLoading && Boolean(user),
    queryFn: async () => {
      let announcements: Announcement[] = []
      let announcementsFailed = false
      try {
        announcements = await announcementsService.listAnnouncements()
      } catch (err) {
        announcementsFailed = true
        reportError(err, { context: 'useNotificationsFeed.listAnnouncements' })
        trackObservability('announcements_fetch_failed', {
          message: err instanceof Error ? err.message : String(err),
        })
      }

      let unreadTicketCount = 0
      let ticketCountFailed = false
      if (user) {
        try {
          unreadTicketCount =
            user.role === 'super_admin'
              ? await supportService.getAdminUnreadCount()
              : await supportService.getUnreadCount()
        } catch (err) {
          ticketCountFailed = true
          reportError(err, { context: 'useNotificationsFeed.unreadTickets' })
          trackObservability('support_unread_count_fetch_failed', {
            role: user.role,
            message: err instanceof Error ? err.message : String(err),
          })
        }
      }

      return {
        announcements,
        unreadTicketCount,
        announcementsFailed,
        ticketCountFailed,
      }
    },
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: () =>
      typeof document !== 'undefined' && document.hidden ? false : 15_000,
  })

  return {
    announcements: query.data?.announcements ?? [],
    unreadTicketCount: query.data?.unreadTicketCount ?? 0,
    loading: query.isPending,
    announcementsFailed: query.data?.announcementsFailed ?? false,
    ticketCountFailed: query.data?.ticketCountFailed ?? false,
    refetch: () => query.refetch(),
  }
}

function dashboardAlertToSurfaceItem(d: DashboardAlert): NotificationSurfaceItem {
  return {
    id: `dashboard:${d.id}`,
    severity: d.severity === 'critical' ? 'critical' : d.severity === 'warning' ? 'warning' : 'info',
    title: d.title,
    body: d.body,
    href: d.cta.href,
    source: 'dashboard',
  }
}

/**
 * Operator-facing aggregate: critical vs informational rows plus shared dashboard-dismiss state.
 */
export function useNotifications(): NotificationsFeed & {
  critical: NotificationSurfaceItem[]
  info: NotificationSurfaceItem[]
  dismissed: Set<string>
  bellDashboardAlerts: DashboardAlert[]
  dismissDashboardAlert: (id: string) => void
} {
  const { user } = useAuth()
  const feed = useNotificationsFeed()

  const dismissedRaw = useSyncExternalStore(
    subscribeDashboardDismissed,
    getDashboardDismissedSnapshot,
    () => '[]',
  )
  const dismissed = useMemo(() => parseDashboardDismissedSnapshot(dismissedRaw), [dismissedRaw])

  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async ({ signal }) => {
      const res = await api.get('/dashboard/stats', { signal })
      return normalizeDashboardStats(parseDashboardStatsPayload(res.data))
    },
    enabled: Boolean(user),
  })

  const dash = useDashboardAlerts({ user, stats: statsQuery.data ?? null })
  const supportHref = user?.role === 'super_admin' ? '/super-admin/tickets' : '/support'

  const critical = useMemo((): NotificationSurfaceItem[] => {
    const out: NotificationSurfaceItem[] = []
    for (const a of feed.announcements) {
      if (a.userState?.dismissedAt) continue
      if (a.severity === 'critical') {
        out.push({
          id: `announcement:${a.id}`,
          severity: 'critical',
          title: a.title,
          body: a.message,
          source: 'announcement',
        })
      }
    }
    for (const d of dash.bellDashboardAlerts) {
      if (d.severity === 'critical') {
        out.push(dashboardAlertToSurfaceItem(d))
      }
    }
    if (feed.unreadTicketCount > 0) {
      out.push({
        id: 'support:unread',
        severity: 'critical',
        title: 'Support inbox',
        body: `${feed.unreadTicketCount} unread ticket thread(s).`,
        href: supportHref,
        source: 'support_unread',
      })
    }
    return out
  }, [feed.announcements, feed.unreadTicketCount, dash.bellDashboardAlerts, supportHref])

  const info = useMemo((): NotificationSurfaceItem[] => {
    const out: NotificationSurfaceItem[] = []
    for (const a of feed.announcements) {
      if (a.userState?.dismissedAt) continue
      if (a.severity === 'info' || a.severity === 'warning') {
        out.push({
          id: `announcement:${a.id}`,
          severity: a.severity === 'warning' ? 'warning' : 'info',
          title: a.title,
          body: a.message,
          source: 'announcement',
        })
      }
    }
    for (const d of dash.bellDashboardAlerts) {
      if (d.severity !== 'critical') {
        out.push(dashboardAlertToSurfaceItem(d))
      }
    }
    return out
  }, [feed.announcements, dash.bellDashboardAlerts])

  return {
    ...feed,
    critical,
    info,
    dismissed,
    bellDashboardAlerts: dash.bellDashboardAlerts,
    dismissDashboardAlert: dash.dismissAlert,
  }
}
